import React from 'react';
import DefaultLayout from '../../layouts/DefaultLayout';
import TemplateColumn, { TemplateColumnWithSidebarContainer } from '../../layouts/TemplateColumn';
import { PageHeader, PageSection, RelatedContent, CrossLink, IndicationPageIntro, AnchorLinks, PrimaryClaim, SecondaryCallout, EndpointDisplay, PageSubSection, PatientProfile, ComparisonTable, ReferencesBlock, KMCurve, TextBlock, FeaturedStat, NCCNcallout, ForestPlot } from '../../components';
import OVARIAN from '../../components/StudyDesign/__study-design/ovarian';
import { jobCodes } from '../../util/data/jobCodes';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { Heading, NotationBlock, WYSIWYG } from '../../util/typography';
import { createMarkup } from '../../util/functions';
 
// SEO INFORMATION
const pageMeta = {
    title: `KEYNOTE-B96 - Clinical Findings & Study Design`,
    keywords: ``,
    description: `Health care professionals may find clinical findings and study design from the KEYNOTE-B96 clinical trial for patients with platinum resistant ovarian cancer for tumors expressing PD-L1.`,
    schemaJsonLD: [
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/ovarian-cancer-keynote-b96/","@type":"ApprovedIndication","name":"KEYTRUDA and KEYTRUDA QLEX™","alternateName":"pembrolizumab and berahyaluronidase alfa-pmph","description":"KEYTRUDA and KEYTRUDA QLEX™ (pembrolizumab and berahyaluronidase alfa-pmph) Injection 165 mg + 2,000 units/mL are each indicated, in combination with paclitaxel, with or without bevacizumab, for the treatment of adult patients with platinum-resistant epithelial ovarian, fallopian tube, or primary peritoneal carcinoma whose tumors express PD-L1 (CPS ≥1) as determined by an FDA-authorized test, and who have received 1 or 2 prior systemic treatment regimens."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/ovarian-cancer-keynote-b96/","@type":"MedicalAudience","audienceType":"US Health Care Professionals","geographicArea":"This site is intended only for residents of the United States and its territories, including Puerto Rico."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/ovarian-cancer-keynote-b96/","@type":"MedicalWebPage","name":"Platinum-Resistant Ovarian Cancer","description":"Combination Therapy for Platinum-Resistant Ovarian Cancer for Tumors Expressing PD-L1 (CPS ≥1)"}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/ovarian-cancer-keynote-b96/","@type":"MedicalTrial","name":"Clinical Findings from KEYNOTE⁠-⁠B96","description":"643 adult patients with platinum-resistant epithelial ovarian, fallopian tube, or primary peritoneal carcinoma with 1 or 2 prior lines of systemic therapy for ovarian carcinoma were evaluated in a phase 3 multicenter, randomized, double-blind, placebo-controlled trial.","trialDesign":"RandomizedTrial"}`
    ]
}
 
//from /src/util/data/indications.js
const indicationId = 41;
const jobCode = jobCodes[42].jobCode;
 
const relatedContent = [
    {
        section: 'Resources',
        link: {
            url: '/resources/mechanism-of-action/',
            label: 'Mechanism of Action'
        }
    },
    {
        section: 'Resources',
        link: {
            url: 'https://www.merckaccessprogram-keytruda.com/',
            label: 'Access & Reimbursement Support'
        }
    }
];
 
const crossLinkContent = {
    sections: [
        {
            heading: "Related Information",
            items: [
                { text: 'Selected Adverse Reactions', url: '/safety/adverse-reactions/ovarian-cancer-keynote-b96/'},
                { text: 'Dosing', url: '/dosing/options/#ova' },
            ]
        },
    ],
    interestLinks: {
        heading: "You Might Be Interested In",
        items: [
            { eyebrow: 'Resources', text: 'Mechanism of Action', url: '/resources/mechanism-of-action/' },
        ],
    }
}
 
const anchorLinkModelData = [
    { label: 'Clinical Findings from KEYNOTE&#8288;-&#8288;B96' },
    { label: 'Study Design for KEYNOTE&#8288;-&#8288;B96' },
]

 
const PfsFootnote = [
    {
        label: "a.",
        text: "Based on stratified log-rank test (<i>P</i> value [one-sided] is compared to an alpha boundary of 0.0116)."
    },
    {
        label: "b.",
        text: "Chemotherapy (paclitaxel) with or without bevacizumab."
    },
] 

const OsFootnote = [
    {
        label: "b.",
        text: "Chemotherapy (paclitaxel) with or without bevacizumab."
    },
    {
        label: "c.",
        text: "Based on stratified log-rank test (<i>P</i> value [one-sided] is compared to an alpha boundary of 0.0083)."
    },
] 
 
const Page = ({ location }) => {
 
    return (
        <DefaultLayout indicationId={indicationId} jobCode={jobCode} pageMeta={pageMeta} location={location}>
            <div data-template-name="template-e">
                {/* Begin Intro Column */}
                <TemplateColumn id="template-e-intro" maxWidth={824}>
                    <PageHeader title="Clinical Trial Results for KEYTRUDA® (pembrolizumab)" isBranding/>
                    <PageSection bgColor="white">
                        <IndicationPageIntro indicationId={indicationId} />
                        <AnchorLinks items={anchorLinkModelData} />
                    </PageSection>
                    <PageSection bgColor="gradient">
                        <PrimaryClaim theme="light">Superior Progression-Free Survival (PFS) and Overall Survival (OS) With KEYTRUDA in Combination With Paclitaxel ± Bevacizumab vs Paclitaxel ± Bevacizumab Alone In Adult Patients With Platinum-Resistant Ovarian Cancer (PROC) Whose Tumors Expressed PD&#8288;-&#8288;L1 (CPS ≥1) in a Clinical Trial</PrimaryClaim>
                    </PageSection>
                </TemplateColumn>
                {/* End Intro Column */}
 
                <TemplateColumnWithSidebarContainer>
                    {/* Begin Main Column */}
                    <TemplateColumn id="template-e-main"> 
                        <PageSection bgColor="cloud" title={anchorLinkModelData[0].label}>
                            <PageSubSection title="PFS in KEYNOTE&#8288;-&#8288;B96">
                            <SecondaryCallout 
                                title='Superior PFS with KEYTRUDA in combination with paclitaxel ± bevacizumab vs paclitaxel ± bevacizumab alone in adult patients with PROC whose tumors expressed PD-L1 (CPS ≥1).'
                                definitions='CPS = combined positive score; PD-L1 = programmed death ligand 1.'
                            />
                           
                             <KMCurve
                                title="Kaplan-Meier Curve for PFS in KEYNOTE-B96 (CPS ≥1)"
                                hr="<strong>HR=0.72 (95% CI, 0.58&#8211;0.89; <i>P</i><sup>a</sup>=0.0014)</strong>"
                                image={() => <StaticImage
                                    placeholder="blurred"
                                    src="../../assets/KM-PFS-Ovarian.png"
                                    alt="Kaplan-Meier Curve for PFS in KEYNOTE-B96 (CPS ≥1)" />}
                                additionalInformation={[
                                    {   text:`<ul>
                                                <li><strong>28% reduction in the risk of disease progression or death</strong> with KEYTRUDA + paclitaxel ± bevacizumab vs placebo + paclitaxel ± bevacizumab <strong>(HR=0.72; 95% CI, 0.58–0.89; <i>P</i><sup>a</sup>=0.0014)</strong></li>
                                             </ul>`
                                    }]
                                }
                            />
                             <ComparisonTable 
                                title='PFS in KEYNOTE&#8288;-&#8288;B96'
                                data={
                                    [
                                        [
                                            {
                                                legendTitle: 'KEYTRUDA 400 mg Q6W + paclitaxel ± bevacizumab (n=234)',
                                            },
                                            {
                                                legendTitle: 'Placebo Q6W + paclitaxel ± bevacizumab (n=232)',
                                            },
                                        ],
                                        [
                                            {
                                                label: 'Number of patients with event (%) ',
                                                dataPoint: '162',
                                                dataSubPoint: "(69%)"
                                            },
                                            {
                                                label: 'Number of patients with event (%) ',
                                                dataPoint: '180',
                                                dataSubPoint: '(78%)',
                                            },
                                        ],                                       
                                    ]
                                }
                                definitions='Q6W = every 6 weeks.'
                            />
                            <FeaturedStat
                                statistic='8.3'
                                duration='months'
                                title='<strong>Median PFS was 8.3 months</strong> (95% CI, 7.0–9.4) with KEYTRUDA + paclitaxel ± bevacizumab vs 7.2 months (95% CI, 6.2–8.1) with placebo + paclitaxel ± bevacizumab' 
                                footnotes={PfsFootnote}
                                definitions='CI = confidence interval; HR = hazard ratio.'
                            />
                            </PageSubSection>
                            <PageSubSection title="OS in KEYNOTE&#8288;-&#8288;B96">
                           {/* <Heading.H3
                                as='H2'
                                dangerouslySetInnerHTML={createMarkup('OS in KEYNOTE&#8288;-&#8288;B96')}
                            /> */}
                            <SecondaryCallout 
                                title='Superior OS with KEYTRUDA in combination with paclitaxel ± bevacizumab vs paclitaxel ± bevacizumab alone in adult patients with PROC whose tumors expressed PD-L1 (CPS ≥1).'
                            />
                            <KMCurve
                                title="Kaplan-Meier Curve for OS in KEYNOTE-B96 (CPS ≥1)"
                                hr="<strong>HR=0.76 (95% CI, 0.61&#8211;0.94; <i>P</i><sup>c</sup>=0.0053)</strong>"
                                image={() => <StaticImage
                                    placeholder="blurred"
                                    src="../../assets/KM-OS-Ovarian.png"
                                    alt="Kaplan-Meier Curve for OS in KEYNOTE-B96 (CPS ≥1)" />}
                                additionalInformation={[
                                    {   text:`<ul>
                                                <li><strong>24% reduction in the risk of death</strong> with KEYTRUDA + paclitaxel ± bevacizumab vs placebo + paclitaxel ± bevacizumab <strong>(HR=0.76; 95% CI, 0.61–0.94; <i>P</i><sup>c</sup>=0.0053)</strong></li>
                                             </ul>`
                                    }]
                                }
                            />
                             <ComparisonTable 
                                title='OS in KEYNOTE&#8288;-&#8288;B96'
                                data={
                                    [
                                        [
                                            {
                                                legendTitle: 'KEYTRUDA 400 mg Q6W + paclitaxel ± bevacizumab (n=234)',
                                            },
                                            {
                                                legendTitle: 'Placebo Q6W + paclitaxel ± bevacizumab (n=232)',
                                            },
                                        ],
                                        [
                                            {
                                                label: 'Number of patients with event (%) ',
                                                dataPoint: '157',
                                                dataSubPoint: "(67%)"
                                            },
                                            {
                                                label: 'Number of patients with event (%) ',
                                                dataPoint: '175',
                                                dataSubPoint: '(75%)',
                                            },
                                        ],                                       
                                    ]
                                }
                            />
                             <FeaturedStat
                                statistic='18.2'
                                title='<strong>Median OS was 18.2 months</strong> (95% CI, 15.3–21.0) with KEYTRUDA + paclitaxel ± bevacizumab vs 14.0 months (95% CI, 12.5–16.1) with placebo + paclitaxel ± bevacizumab' 
                                footnotes={OsFootnote}
                                duration="months"
                            />
                            </PageSubSection>
                        </PageSection>
 
 
                        <PageSection bgColor="white" title={anchorLinkModelData[1].label}>
                            <OVARIAN />
                        </PageSection>
 
                    </TemplateColumn>
                    {/* End Main Column */}
                    <TemplateColumn id="template-e-sidebar">
                        <PageSection bgColor="mist">
                            <CrossLink {...crossLinkContent} />
                        </PageSection>
                    </TemplateColumn>
 
                </TemplateColumnWithSidebarContainer>
 
                <TemplateColumn id="template-e-secondary">
                    <PageSection bgColor="keytrudaGreen">
                        <RelatedContent items={relatedContent} />
                    </PageSection>
                    {/* <PageSection bgColor="mist">
                        <ReferencesBlock />
                    </PageSection> */}
                </TemplateColumn>
            </div>
        </DefaultLayout>
    );
};
 
export default Page;
 